import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';

const RESTRICTED = 'restricted';

export interface AuthData extends JwtPayload {
  scope: Array<string> | undefined;
  accountId: string;
  country: string;
  user_name: string;
  authentication_type: string;
  client_id: string;
}

export const TOKEN_STATUSES = {
  noToken: 'noToken',
  notVerified: 'noVerified',
  tokenExpired: 'TokenExpired',
  tokenValid: 'tokenValid',
} as const;

/**
 * This number will be subtracted from the token exp date to have safe margin to refresh token.
 * @type {number}
 */
const EXPIRATION_SAFE_MARGIN_SECS: number = 60;

export class AuthManager {
  _localStorageService: Storage;
  _storageKey: string;

  static _getTokenData(token: string) {
    return jwtDecode<AuthData>(token);
  }

  static getTokenState(token: string | undefined | null) {
    if (!token) {
      return TOKEN_STATUSES.noToken;
    }
    const { exp, scope } = AuthManager._getTokenData(token);
    const isRestricted = Array.isArray(scope) && scope.includes(RESTRICTED);
    // now - 1 minute > expiration date
    const hasExpired = exp
      ? Date.now() / 1000 > exp - EXPIRATION_SAFE_MARGIN_SECS
      : true;

    if (isRestricted) {
      return TOKEN_STATUSES.notVerified;
    }
    if (hasExpired) {
      return TOKEN_STATUSES.tokenExpired;
    }
    return TOKEN_STATUSES.tokenValid;
  }

  constructor(
    localStorageService: Storage,
    storageKey = 'customer_authentication',
  ) {
    this._localStorageService = localStorageService;
    this._storageKey = storageKey;
  }

  getDetailsFromToken(token: string) {
    if (!token) {
      return {
        email: '',
        accountId: '',
        tokenState: this.getTokenState(token),
        token: '',
      };
    }
    const { accountId, user_name, authentication_type } =
      AuthManager._getTokenData(token);
    return {
      accountId,
      email: user_name,
      tokenState: this.getTokenState(token),
      authType: authentication_type,
      token,
    };
  }

  /**
   * Get token state
   * @param token
   * @return {string}
   */
  // eslint-disable-next-line class-methods-use-this
  getTokenState(token?: string | undefined | null): string {
    return AuthManager.getTokenState(token);
  }

  /**
   * Get customer authentication object
   */
  getCustomerAuthentication(): Record<string, string> {
    return JSON.parse(
      this._localStorageService.getItem(this._storageKey) || '{}',
    );
  }

  /**
   * Get refresh token
   * @return {string|null}
   */
  getRefreshToken(): string | null {
    return this.getCustomerAuthentication().refresh_token || null;
  }

  /**
   * Get access token
   * @return {string|null}
   */
  getAccessToken(): string | null {
    return this.getCustomerAuthentication().access_token || null;
  }

  /**
   * Remove auth
   * @return {void}
   */
  removeAuth(): void {
    this._localStorageService.removeItem(this._storageKey);
  }

  /**
   * Override local storage item
   * @param data
   */
  writeToCustomerAuth(data = {}): void {
    this._localStorageService.setItem(this._storageKey, JSON.stringify(data));
  }

  /**
   * Merge authentication data in local storage
   * @param data
   * @param authData
   */
  mergeCustomerAuth(data = {}, authData = this.getCustomerAuthentication()) {
    const newData = Object.assign({}, authData, data);
    this.writeToCustomerAuth(newData);
  }
}
